import { FloatingWidgetButton } from "@circle-react-shared/FloatingWidgetButton";
import {
  useIsCommunityAdmin,
  usePunditUserContext,
} from "@circle-react/contexts/punditUserContext";
import { chatRoomsPath } from "@circle-react/helpers/urlHelpers";
import { aiAgentQueries } from "@circle-react/lib/queries/aiAgents";
import { communityInboxChatQueries } from "@circle-react/lib/queries/communityInboxChats";
import { Avatar } from "@circleco/compass/components/Avatar";
import { Icon } from "@circleco/compass/components/Icon";
import { useLocation } from "@tanstack/react-router";
import { useQuery } from "react-query";

import { t } from "@/i18n-js/instance";

import { useAvatarConversationPanelStore } from "../useAvatarConversationPanelStore";
import { useIsAvatarsV2Layout } from "../useIsAvatarsV2Layout";

export const LatestAvatarChatButton = () => {
  const { currentCommunityMember } = usePunditUserContext();
  const isAdmin = useIsCommunityAdmin();
  const isAvatarsV2UiEnabled = useIsAvatarsV2Layout();
  const isInsideMessages = useLocation({
    select: location => location.pathname.startsWith(chatRoomsPath()),
  });
  const canFetchChats =
    isAvatarsV2UiEnabled && !!currentCommunityMember && !isInsideMessages;

  const { data: chat, isSuccess: isLatestChatLoaded } = useQuery({
    ...communityInboxChatQueries.latestAvatarChat(),
    enabled: canFetchChats,
  });
  const hasChat = !!chat;
  const { data: avatars } = useQuery({
    ...aiAgentQueries.memberAvatars(),
    enabled: canFetchChats && isLatestChatLoaded && !hasChat,
  });
  const hasStartableAvatar = !!avatars?.length;

  const isPanelOpen = useAvatarConversationPanelStore(
    s => s.panelState !== "closed",
  );
  const openPanel = useAvatarConversationPanelStore(s => s.openPanel);
  const showAvatars = useAvatarConversationPanelStore(s => s.showAvatars);
  const isWidgetCollapsed = useAvatarConversationPanelStore(
    s => s.isWidgetCollapsed,
  );
  const toggleWidgetCollapsed = useAvatarConversationPanelStore(
    s => s.toggleWidgetCollapsed,
  );

  const shouldShowButton =
    isAvatarsV2UiEnabled &&
    !isAdmin &&
    !isInsideMessages &&
    !isPanelOpen &&
    (hasChat || hasStartableAvatar);

  if (!shouldShowButton) return null;

  const profile = chat?.community_bot_profile;
  const avatar = profile && (
    <Avatar name={profile.name} src={profile.avatar_url} size="lg" decorative />
  );
  const genericIcon = (
    <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-secondary">
      <Icon name="message-dots" />
    </span>
  );

  const handleClick = () => {
    if (chat) {
      openPanel({ chatId: chat.id });
      return;
    }
    openPanel();
    showAvatars();
  };

  return (
    <div className="fixed bottom-6 right-6 z-10">
      <FloatingWidgetButton
        expanded={hasChat && !isWidgetCollapsed}
        onToggle={chat && toggleWidgetCollapsed}
        label={profile?.name ?? t("avatars.conversation_panel.open_chat")}
        leading={avatar ?? genericIcon}
        onClick={handleClick}
        unreadCount={chat?.unread_messages_count}
      />
    </div>
  );
};
